import { useState } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import UnderlineLink from '@comp/UnderlineLink/UnderlineLink';
import Button from '@base/Button/Button';
import H2 from '@base/H2/H2';
import H6 from '@base/H6/H6';
import P1 from '@base/P1/P1';
import TextField from '@base/TextField/TextField';
import useInterval from '@hooks/useInterval';
import { useIsMobile } from '@stores/AppCommonData';
import { Spacings, Fonts } from '../../../styleConstants';

const StyledP1 = styled(P1)`
  margin-top: ${Spacings.SPACING_3B};
  color: var(--color-text-secondary);
`;

const EmailTextField = styled(TextField)`
  margin-top: ${Spacings.SPACING_8B};
  background: var(--color-base-1);
  color: var(--color-text-primary);
`;

const OtpTextField = styled(TextField)`
  margin-top: ${Spacings.SPACING_4B};
`;

const SaveButton = styled(Button)`
  margin-top: ${Spacings.SPACING_8B};
`;

const StyledH6 = styled(H6)`
  font-weight: ${Fonts.BOLD};
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${Spacings.SPACING_8B};
`;

const validateNumber = (event) => {
  const { target } = event;
  target.value = target.value.replace(/[^0-9]/g, '');
};

const EnterEmail = ({
  fieldsData,
  showCreatePasswordModal,
  setFieldsData,
  submitEmail,
  submitEmailOtp,
  sendEmailOtpStatus,
  verifyEmailOtpStatus,
  resetNetworkCallsError,
  buttonTheme
}) => {
  const [timeLeft, setTimeLeft] = useState(60);
  const startTimer = () => {
    setTimeLeft(60);
  };
  const isResendDisabled = timeLeft > 0;

  const onFieldValueChange = (e) => {
    const { name, value } = e.target;
    setFieldsData({
      ...fieldsData,
      [name]: {
        ...fieldsData[name],
        value: value.replace(/ /g, '')
      }
    });
    resetNetworkCallsError();
  };

  const handleContinueClick = () => {
    startTimer();
    submitEmail();
  };

  const handleResendClick = () => {
    if (!isResendDisabled) {
      startTimer();
      submitEmail(true);
    }
  };

  const getTimeLeftStr = () => {
    if (timeLeft > 0) {
      const str = `0${timeLeft}`;
      return ` in 00:${String(str).slice(-2)}`;
    }
    return '';
  };

  useInterval(
    () => {
      setTimeLeft(timeLeft - 1);
    },
    timeLeft > 0 ? 1000 : null
  );

  const email = fieldsData.email.value;
  const emailOtp = fieldsData.emailOtp.value;
  const showOtpField = fieldsData.emailOtp.show;
  const isMobile = useIsMobile();

  return (
    <>
      {!showCreatePasswordModal && (
        <>
          <H2 color="var(--color-text-primary)">
            {!showOtpField ? 'Edit Email' : 'Enter OTP'}
          </H2>

          {showOtpField && (
            <StyledP1>
              We&apos;ve sent an OTP to your updated e-mail address
            </StyledP1>
          )}

          <EmailTextField
            name="email"
            fullWidth={!isMobile}
            placeholder="Email"
            onChange={onFieldValueChange}
            value={email}
            inputProps={{
              type: 'email',
              required: true
            }}
            type="email"
            autoFocus
            disabled={showOtpField}
            error={sendEmailOtpStatus.errorMsg}
            helperText={sendEmailOtpStatus.errorMsg}
            color="var(--color-text-primary)"
          />

          {showOtpField && (
            <OtpTextField
              name="emailOtp"
              fullWidth
              placeholder="OTP"
              onChange={onFieldValueChange}
              value={emailOtp}
              inputProps={{
                type: 'tel',
                maxLength: 6,
                required: true
              }}
              onInput={validateNumber}
              type="tel"
              autoFocus
              error={verifyEmailOtpStatus.errorMsg}
              helperText={verifyEmailOtpStatus.errorMsg}
            />
          )}

          {!showOtpField ? (
            <SaveButton
              variant={
                !email || sendEmailOtpStatus.isLoading ? 'disabled' : 'filled'
              }
              label={!sendEmailOtpStatus.isLoading ? 'Continue' : 'Sending OTP'}
              onClick={
                !sendEmailOtpStatus.isLoading ? handleContinueClick : null
              }
              color={buttonTheme}
            />
          ) : (
            <ButtonWrapper>
              <Button
                variant={
                  !emailOtp || verifyEmailOtpStatus.isLoading
                    ? 'disabled'
                    : 'filled'
                }
                label={
                  !verifyEmailOtpStatus.isLoading
                    ? 'Verify OTP'
                    : 'Verifying OTP'
                }
                onClick={
                  !verifyEmailOtpStatus.isLoading ? submitEmailOtp : null
                }
                color={buttonTheme}
              />

              <UnderlineLink
                disabled
                onClick={handleResendClick}
                color={
                  timeLeft > 0
                    ? 'var(--color-text-secondary)'
                    : 'var(--color-text-primary)'
                }
              >
                <StyledH6
                  color={
                    timeLeft > 0
                      ? 'var(--color-text-secondary)'
                      : 'var(--color-text-primary)'
                  }
                  fixedWidth={timeLeft > 0}
                >
                  {`Resend OTP ${getTimeLeftStr()}`}
                </StyledH6>
              </UnderlineLink>
            </ButtonWrapper>
          )}
        </>
      )}
    </>
  );
};

export default EnterEmail;

EnterEmail.propTypes = {
  fieldsData: PropTypes.objectOf(PropTypes.any).isRequired,
  setFieldsData: PropTypes.func.isRequired,
  submitEmail: PropTypes.func.isRequired,
  submitEmailOtp: PropTypes.func.isRequired,
  sendEmailOtpStatus: PropTypes.objectOf(PropTypes.any).isRequired,
  verifyEmailOtpStatus: PropTypes.objectOf(PropTypes.any).isRequired,
  showCreatePasswordModal: PropTypes.bool.isRequired,
  resetNetworkCallsError: PropTypes.func.isRequired,
  buttonTheme: PropTypes.string
};

EnterEmail.defaultProps = {
  buttonTheme: 'green'
};
